import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CpuIcon, ClockCountdownIcon, CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";

function fmtDuration(minutes) {
  if (!minutes || minutes < 1) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m ago`;
}

export default function CycleProgressTile() {
  const [p, setP] = useState(null);

  useEffect(() => {
    const load = () => api.get("/progress/current").then((r) => setP(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!p || p.active === false) {
    return (
      <div data-testid="cycle-progress-idle" className="border border-[#2A2D3A] bg-[#15171E] rounded-lg p-4 flex items-center gap-3">
        <WarningCircleIcon size={18} className="text-[#64748B]" />
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Cycle Progress</div>
          <div className="mono text-xs text-[#94A3B8] mt-1">
            No live heartbeat. Start <code className="text-[#D6FF38]">progress_reporter.ps1</code> on the VPS.
          </div>
        </div>
      </div>
    );
  }

  const pct = p.bots_total > 0 ? Math.round((p.bots_complete / p.bots_total) * 100) : 0;
  const stalePenalty = p.stale_seconds > 120;

  return (
    <div data-testid="cycle-progress-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-[#2A2D3A] bg-[#0F1116] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CpuIcon size={16} className="text-[#D6FF38]" />
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#D6FF38]">Cycle Progress</div>
        </div>
        <div className={`mono text-[9px] ${stalePenalty ? 'text-[#F97316]' : 'text-[#2ECA7F]'}`}>
          heartbeat {fmtRelative(p.updated_at)}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
            <span>Batch progress</span>
            <span data-testid="progress-count">{p.bots_complete || 0} / {p.bots_total || 0} bots · {pct}%</span>
          </div>
          <div className="h-2 rounded bg-[#0F1116] overflow-hidden border border-[#2A2D3A]">
            <div className="h-full bg-gradient-to-r from-[#D6FF38] to-[#22D3EE]" style={{width: `${pct}%`}} />
          </div>
        </div>

        {/* Now backtesting */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded p-2">
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Now Backtesting</div>
            <div className="mono text-xs text-[#F1F5F9] mt-1 truncate" data-testid="progress-current-bot">
              {p.current_bot || "—"}
            </div>
          </div>
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded p-2">
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Symbol</div>
            <div className="mono text-xs text-[#22D3EE] mt-1" data-testid="progress-symbol">
              {p.current_symbol || "—"}
            </div>
          </div>
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded p-2">
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Month</div>
            <div className="mono text-xs text-[#F1F5F9] mt-1">
              {p.month_index || 0}/{p.month_total || 19} · {p.current_month || "—"}
            </div>
          </div>
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded p-2">
            <div className="flex items-center gap-1">
              <ClockCountdownIcon size={11} className="text-[#E879F9]" />
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">ETA</div>
            </div>
            <div className="mono text-xs text-[#E879F9] mt-1" data-testid="progress-eta">
              {fmtDuration(p.eta_minutes)}
            </div>
          </div>
        </div>

        {/* Last completed */}
        {p.last_completed_bot && (
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded p-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircleIcon size={14} className="text-[#2ECA7F]" />
              <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Last completed</div>
            </div>
            <div className="mono text-xs">
              <span className="text-[#F1F5F9] truncate mr-2">{p.last_completed_bot}</span>
              {p.last_completed_growth != null && (
                <span className={p.last_completed_growth >= 0 ? "text-[#2ECA7F]" : "text-[#FF4D4D]"}>
                  {p.last_completed_growth >= 0 ? "+" : ""}{p.last_completed_growth}%
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
